import { useRef } from 'react';
import { Slider } from '@/components/ui/slider';

// Shared pointer mapping keeps the full 44px target usable at every canvas/viewport size.
export const TouchSlider = ({ value, min, max, step = 1, disabled, label, testId, onChange, onStart, onEnd }) => {
  const ref = useRef(null);
  const pointer = useRef(null);
  const end = () => { pointer.current = null; onEnd?.(); };
  const update = event => {
    const rect = ref.current.getBoundingClientRect();
    const fraction = (event.clientX - pointer.current.offset - rect.left - 22) / Math.max(1, rect.width - 44);
    const raw = min + fraction * (max - min);
    const snapped = Math.min(max, Math.max(min, min + Math.round((raw - min) / step) * step));
    onChange(Number(snapped.toFixed(3)));
  };
  const start = event => {
    event.preventDefault();
    if (disabled || pointer.current || (event.pointerType === 'mouse' && event.button !== 0)) return;
    const thumb = ref.current.querySelector('[role="slider"]');
    const bounds = thumb.getBoundingClientRect();
    const offset = thumb.contains(event.target) ? event.clientX - bounds.left - bounds.width / 2 : 0;
    // Values only change after a deliberate horizontal drag (or a clean tap); vertical swipes keep scrolling.
    pointer.current = { id: event.pointerId, offset, x: event.clientX, y: event.clientY, active: false, target: event.currentTarget, thumb };
  };
  const move = event => {
    const p = pointer.current;
    if (p?.id !== event.pointerId) return;
    if (!p.active) {
      const dx = Math.abs(event.clientX - p.x), dy = Math.abs(event.clientY - p.y);
      if (dy > 10 && dy > dx) { pointer.current = null; return; }
      if (dx < 6 || dx < dy) return;
      p.active = true; p.thumb.focus(); p.target.setPointerCapture(event.pointerId); onStart?.();
    }
    event.preventDefault(); update(event);
  };
  const stop = event => {
    const p = pointer.current;
    if (p?.id !== event.pointerId) return;
    event.preventDefault();
    if (!p.active && Math.hypot(event.clientX - p.x, event.clientY - p.y) < 6) { p.thumb.focus(); onStart?.(); update(event); }
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    end();
  };
  return <Slider ref={ref} className="curve-slider" min={min} max={max} step={step} value={[value]} disabled={disabled} thumbLabel={label} thumbTestId={`${testId}-thumb`} data-testid={testId} onPointerDown={start} onPointerMove={move} onPointerUp={stop} onPointerCancel={stop} onLostPointerCapture={() => { if (pointer.current) end(); }} onKeyDown={onStart} onKeyUp={onEnd} onBlur={end} onValueChange={([v]) => { onStart?.(); onChange(v); }} />;
};